import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, ExtensionOutlined, RefreshOutlined } from '@mui/icons-material';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useAdminCommercialFeaturesQuery } from '@/state/queries/admin/use-admin-commercial-features-query';
import { useDeleteCommercialFeatureMutation } from '@/state/mutations/admin-commercial-features/use-delete-commercial-feature-mutation';
import CommercialFeatureAdminFormModal from '@/components/admin-commercial-features/commercial-feature-admin-form.modal';
import type { CommercialFeature } from '@/types/dtos/commercial-features/commercial-feature.dto';

export default function CommercialFeaturesAdminPage() {
  const { data, isLoading, error, refetch, isFetching } = useAdminCommercialFeaturesQuery();
  const features = data?.features ?? [];
  const deleteMut = useDeleteCommercialFeatureMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CommercialFeature | null>(null);

  const handleDelete = async (f: CommercialFeature) => {
    if (!window.confirm(`Delete commercial feature “${f.key}” (${f.displayName})? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteMut.mutateAsync(f.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ExtensionOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Commercial features (admin)
                  </Typography>
                  <IconButton size="small" aria-label="Refresh" onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                  </IconButton>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditing(null);
                    setModalOpen(true);
                  }}
                >
                  Create entry
                </Button>
              </Box>
            }
          />

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 0 }}>
              {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {(error as Error).message}
                </Alert>
              )}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!isLoading && !features.length && (
                <EmptyState text="No commercial feature keys yet. Create entries that match subscription plan featureKeys and FEATURE/PACKAGE products." />
              )}
              {!isLoading && !!features.length && (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Display name</TableCell>
                        <TableCell>Scopes</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {features.map((f) => (
                        <TableRow key={f.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {f.key}
                            </Typography>
                          </TableCell>
                          <TableCell>{f.displayName}</TableCell>
                          <TableCell>
                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                              {f.allowedScopeTypes.map((s) => (
                                <Chip key={s} size="small" label={s} variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>{f.category ?? '—'}</TableCell>
                          <TableCell>
                            {f.deprecated ? (
                              <Chip size="small" label="Deprecated" color="warning" />
                            ) : (
                              <Chip size="small" label="Active" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Edit"
                              onClick={() => {
                                setEditing(f);
                                setModalOpen(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={() => handleDelete(f)}
                              disabled={deleteMut.isPending}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <CommercialFeatureAdminFormModal
            key={editing?.id ?? 'new'}
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditing(null);
            }}
            feature={editing}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, px: 1 }}>
            Keys must exist here before they can be used on subscription plans or products. Requires admin role.
          </Typography>
        </Box>
      }
    />
  );
}
